import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  FaTrophy, FaClock, FaMedal, FaUsers, FaPlay,
  FaLock, FaCrown
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import Avatar from '../components/UI/Avatar';
import Button from '../components/UI/Button';
import PageTransition from '../components/UI/PageTransition';
import api from '../api/axios';
import { useAuth } from '../context/AuthContext';


function useCountdown(endsAt) {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const end = typeof endsAt === 'number' ? endsAt : new Date(endsAt).getTime();
    const update = () => {
      const diff = end - Date.now();
      if (diff <= 0) { setTimeLeft('Terminé'); clearInterval(t); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setTimeLeft(h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endsAt]);
  return timeLeft;
}

function TournamentCard({ tournament, onJoin, isPremiumUser, onRequirePremium }) {
  const countdown = useCountdown(tournament.endsAt);
  const locked = tournament.premium && !isPremiumUser;
  const fillPercent = Math.min(
    100,
    Math.round((tournament.participants / Math.max(1, tournament.maxParticipants)) * 100)
  );

  return (
    <motion.div
      className="card border-green-800"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <h3
              className="text-white font-black text-base"
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {tournament.nom}
            </h3>
            {tournament.premium && (
              <span
                className="bg-qfl-gold/20 text-qfl-gold text-xs px-2 py-0.5 rounded-full border border-qfl-gold/30 font-bold uppercase"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Premium
              </span>
            )}
          </div>
          <p className="text-white/50 text-xs">{tournament.description}</p>
        </div>
        <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
          <div className="flex items-center gap-1 text-orange-400 text-xs font-semibold">
            <FaClock size={10} />
            {countdown}
          </div>
          <div className="text-white/40 text-xs flex items-center gap-1">
            <FaUsers size={10} />
            {tournament.participants}/{tournament.maxParticipants}
          </div>
        </div>
      </div>

      <div className="h-1.5 bg-white/10 rounded-full mb-3 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{
            width: `${fillPercent}%`,
            background: fillPercent > 80 ? '#ef4444' : fillPercent > 50 ? '#f97316' : '#22c55e',
          }}
        />
      </div>

      <div className="flex gap-2 mb-3 flex-wrap">
        {tournament.prix.slice(0, 3).map((p) => (
          <div key={p.place} className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1">
            <span className="text-sm">{p.icon}</span>
            <span className="text-white/60 text-xs">{p.label}</span>
          </div>
        ))}
      </div>

      {/* Message "championnat pas encore commencé" */}
      {tournament.statut === 'bientot' && (
        <div className="rounded-lg px-3 py-2 mb-3 flex items-start gap-2"
          style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)' }}>
          <span className="text-yellow-400 text-sm flex-shrink-0">⏳</span>
          <p className="text-yellow-400/80 text-xs leading-relaxed">
            <strong>Le championnat n'a pas encore commencé.</strong> Tu peux t'inscrire maintenant,
            mais tes parties ne seront comptabilisées qu'à partir du{' '}
            <strong>{new Date(tournament.startsAt).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })}</strong>.
          </p>
        </div>
      )}

      {/* Frais d'entrée */}
      {tournament.entry_fee > 0 && (
        <div className="flex items-center justify-between rounded-lg px-3 py-2 mb-3"
          style={{ background: 'rgba(240,180,41,0.08)', border: '1px solid rgba(240,180,41,0.2)' }}>
          <span className="text-white/50 text-xs">Frais d'entrée</span>
          <span className="text-qfl-gold font-black text-sm" style={{ fontFamily: "'Rajdhani', sans-serif" }}>
            🪙 {tournament.entry_fee} pièces
          </span>
        </div>
      )}

      <Button
        variant={locked ? 'secondary' : tournament.statut === 'actif' ? 'gold' : 'secondary'}
        size="md"
        fullWidth
        disabled={tournament.statut === 'termine'}
        onClick={() => (locked ? onRequirePremium() : onJoin(tournament))}
      >
        {locked ? (
          <><FaCrown size={14} /> RÉSERVÉ PREMIUM</>
        ) : tournament.statut === 'bientot' ? (
          tournament.entry_fee > 0
            ? <><FaClock size={14} /> S'INSCRIRE · 🪙 {tournament.entry_fee}</>
            : <><FaClock size={14} /> S'INSCRIRE</>
        ) : tournament.entry_fee > 0 ? (
          <><FaPlay size={14} /> REJOINDRE · 🪙 {tournament.entry_fee}</>
        ) : (
          <><FaPlay size={14} /> REJOINDRE</>
        )}
      </Button>
    </motion.div>
  );
}

function Tournament() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTournament, setSelectedTournament] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming', 'active', 'myTournaments', 'results', 'manage'
  const isPremium = user?.plan === 'premium' || user?.role === 'admin';

  // Filtrer les championnats par onglet
  const getFilteredTournaments = () => {
    const now = new Date();

    switch (activeTab) {
      case 'upcoming':
        return tournaments.filter(t => new Date(t.startsAt) > now && t.statut === 'bientot');
      case 'active':
        return tournaments.filter(t => t.statut === 'actif');
      case 'myTournaments':
        // Afficher tous les championnats de l'utilisateur (actuels ou passés)
        return tournaments; // À améliorer: charger les données du joueur pour filtrer correctement
      case 'results':
        return tournaments.filter(t => t.statut === 'termine');
      case 'manage':
        return user?.role === 'admin' ? tournaments : [];
      default:
        return tournaments;
    }
  };

  // Rafraîchit le profil depuis la DB au chargement — évite le token stale
  useEffect(() => {
    api.get('/auth/me').then(({ data }) => {
      if (data.user) updateUser(data.user);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    api.get('/tournaments')
      .then(({ data }) => setTournaments(data.tournaments || []))
      .catch(() => setTournaments([]))
      .finally(() => setLoading(false));
  }, []);

  const handleRequirePremium = () => {
    toast('Ce championnat est réservé aux membres Premium', { icon: '👑' });
    navigate('/shop');
  };

  const handleJoin = async (tournament) => {
    if (tournament.premium && !isPremium) {
      handleRequirePremium();
      return;
    }
    if (tournament.entry_fee > 0) {
      const ok = window.confirm(
        `⚠️ L'inscription à "${tournament.nom}" coûte ${tournament.entry_fee} pièces.\n\nConfirmer ?`
      );
      if (!ok) return;
    }
    try {
      await api.post(`/tournaments/${tournament._id}/join`);
      if (tournament.entry_fee > 0) {
        toast.success(`Inscrit ! 🪙 ${tournament.entry_fee} pièces déduites.`);
        updateUser({ ...user, coins: (user?.coins || 0) - tournament.entry_fee });
      } else {
        toast.success(`Inscrit à "${tournament.nom}" !`);
      }
    } catch (err) {
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Erreur lors de l\'inscription');
      if (err?.response?.status !== 409) return;
    }

    // Charger le classement
    try {
      const { data } = await api.get(`/tournaments/${tournament._id}/leaderboard`);
      setLeaderboard(data.leaderboard || []);
    } catch {
      setLeaderboard([]);
    }
    setSelectedTournament(tournament);
  };

  return (
    <PageTransition variant="slideIn">
      <div className="app-container flex flex-col min-h-screen" style={{ paddingBottom: '80px' }}>
        <div className="flex items-center justify-center gap-3 pt-8 pb-4 px-4">
          <FaTrophy
            size={26}
            className="text-qfl-gold"
            style={{ filter: 'drop-shadow(0 0 10px rgba(240,180,41,0.4))' }}
          />
          <h1
            className="text-2xl font-black uppercase tracking-widest text-qfl-gold"
            style={{ fontFamily: "'Rajdhani', sans-serif" }}
          >
            CHAMPIONNATS
          </h1>
        </div>

        {/* Onglets */}
        <div className="flex gap-2 px-4 pb-4 overflow-x-auto" style={{ scrollBehavior: 'smooth' }}>
          {[
            { id: 'upcoming', label: '🔜 À venir' },
            { id: 'active', label: '⚽ En cours' },
            { id: 'myTournaments', label: '🏆 Mes championnats' },
            { id: 'results', label: '📊 Résultats' },
            ...(user?.role === 'admin' ? [{ id: 'manage', label: '⚙️ Gestion' }] : []),
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 rounded-lg font-bold text-sm whitespace-nowrap transition-colors ${
                activeTab === tab.id
                  ? 'bg-qfl-gold text-qfl-dark'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
              style={{ fontFamily: "'Rajdhani', sans-serif" }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-4 flex flex-col gap-4">
          {loading ? (
            <div className="flex justify-center py-10">
              <div className="w-10 h-10 rounded-full border-4 border-qfl-gold border-t-transparent animate-spin" />
            </div>
          ) : getFilteredTournaments().length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center justify-center py-16 text-center"
            >
              <FaTrophy size={40} className="text-white/15 mb-4" />
              <p
                className="text-white/40 font-bold uppercase tracking-widest text-sm mb-1"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                Aucun championnat
              </p>
              <p className="text-white/25 text-xs">
                {activeTab === 'upcoming' && 'Reviens bientôt pour les prochains championnats'}
                {activeTab === 'active' && 'Aucun championnat en cours pour le moment'}
                {activeTab === 'myTournaments' && 'Tu n\'as pas encore participé à de championnats'}
                {activeTab === 'results' && 'Aucun résultat disponible'}
              </p>
            </motion.div>
          ) : (
            getFilteredTournaments().map((t) => (
              <TournamentCard
                key={t._id}
                tournament={t}
                onJoin={handleJoin}
                isPremiumUser={isPremium}
                onRequirePremium={handleRequirePremium}
              />
            ))
          )}

          {selectedTournament && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4"
            >
              <h3
                className="text-qfl-gold font-bold uppercase tracking-widest text-sm mb-3"
                style={{ fontFamily: "'Rajdhani', sans-serif" }}
              >
                <FaMedal className="inline mr-2" />
                CLASSEMENT — {selectedTournament.nom}
              </h3>

              <div className="flex flex-col gap-2 pb-4">
                {leaderboard.map((player, i) => (
                  <div
                    key={i}
                    className={`flex items-center gap-3 rounded-xl px-4 py-3 border ${
                      player.isMe
                        ? 'bg-qfl-gold/10 border-qfl-gold/30'
                        : 'bg-white/5 border-white/10'
                    }`}
                  >
                    <span
                      className="w-6 text-center font-black text-sm"
                      style={{
                        fontFamily: "'Rajdhani', sans-serif",
                        color: i === 0 ? '#f0b429' : i === 1 ? '#94a3b8' : i === 2 ? '#d97706' : '#6b7280',
                      }}
                    >
                      {i + 1}
                    </span>
                    <Avatar user={player} size="xs" />
                    <div className="flex-1">
                      <span
                        className={`font-bold text-sm ${player.isMe ? 'text-qfl-gold' : 'text-white'}`}
                        style={{ fontFamily: "'Rajdhani', sans-serif" }}
                      >
                        {player.pseudo}
                      </span>
                    </div>
                    <span
                      className="font-black text-qfl-gold"
                      style={{ fontFamily: "'Rajdhani', sans-serif" }}
                    >
                      {player.score} pts
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </PageTransition>
  );
}

export default Tournament;
